import { approvalChoiceUserText } from "@/components/assistant/approvalChoiceUserText";
import { AssistantChatEmptyState } from "@/components/assistant/AssistantChatEmptyState";
import { AssistantChatErrorNotice } from "@/components/assistant/AssistantChatErrorNotice";
import { AssistantPendingProposalComposerExtras } from "@/components/assistant/AssistantPendingProposalComposerExtras";
import { AssistantWriteApprovalThreadMessage } from "@/components/assistant/AssistantWriteApprovalThreadMessage";
import { isActivePendingWriteToolPart } from "@/components/assistant/isActivePendingWriteToolPart";
import { useAssistantPlan } from "@/components/assistant/AssistantPlanContext";
import { AssistantPanelPlanSplit } from "@/components/assistant/AssistantPanelPlanSplit";
import { submitAssistantChatMessage } from "@/components/assistant/submitAssistantChatMessage";
import { useAssistantWriteApprovalHandlers } from "@/components/assistant/useAssistantWriteApprovalHandlers";
import { useSyncAssistantPlan } from "@/components/assistant/useSyncAssistantPlan";
import { findActiveWriteApproval } from "@/components/assistant/findActiveWriteApproval";
import { useAutoContinueTextOnlyWriteProposal } from "@/components/assistant/useAutoContinueTextOnlyWriteProposal";
import { applyWriteApprovalSuccessMessageIfNeeded } from "@/components/assistant/applyWriteApprovalSuccessMessage";
import { isHiddenChatUiMessage, WRITE_APPROVAL_CHOICE_MESSAGE_METADATA } from "@/components/assistant/hiddenChatUiMessages";
import { parseWriteAssistantApproveResponse } from "@/components/assistant/parseWriteAssistantApproveResponse";
import { planDraftReadyForDirectWrite } from "@/components/assistant/writeApprovalEditablePlanFields";
import { useWriteApprovalPlanDraftSync } from "@/components/assistant/useWriteApprovalPlanDraftSync";
import { useResolvedWriteApprovals } from "@/components/assistant/useResolvedWriteApprovals";
import { createWriteToolCallApprovalStreamHandler } from "@/components/assistant/applyWriteToolCallApprovalFromStreamPart";
import { useMergePendingWriteApprovals } from "@/components/assistant/useMergePendingWriteApprovals";
import { useAppendUserChatMessage } from "@/components/assistant/useAppendUserChatMessage";
import { ChatMessageBubble } from "@/components/chat/ChatMessageBubble";
import { MessageComposer } from "@/components/chat/MessageComposer";
import { apiUrl } from "@/lib/api-config";
import { getJwtToken } from "@/lib/auth-client";
import { dedupeUiMessagesForApiRequest } from "@/components/assistant/dedupeUiMessagesForApiRequest";
import { narrowUiMessagesForMastraThreadChat } from "@/components/assistant/narrowUiMessagesForMastraThreadChat";
import { finalizeWriteApprovalStreamMessages } from "@/components/assistant/finalizeWriteApprovalStreamMessages";
import { isUserRejectedWriteToolOutput } from "@/components/assistant/isUserRejectedWriteToolOutput";
import { resetWriteAssistantThread } from "@/components/assistant/resetWriteAssistantThread";
import { assistantTextForChatDisplay } from "@/components/assistant/stripWriteApprovalProposalTable";
import { mapAssistantChatUserError, sanitizeAssistantChatDisplayText } from "@/lib/assistant-chat-user-errors";
import { cn } from "@/lib/utils";
import type { ChatMessage } from "@/types";
import type { DraftClauseAlternative, DraftTemplateSectionOption } from "@/types/drafting";
import { IconButton } from "@doculegis/shared";
import { useChat } from "@ai-sdk/react";
import {
  DefaultChatTransport,
  isToolUIPart,
  getToolName,
  type UIMessage,
} from "ai";
import { Loader2, Sparkles, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DraftClauseLibraryProposalCard } from "./DraftClauseLibraryProposalCard";
import { DraftClauseLibraryResultCard } from "./DraftClauseLibraryResultCard";
import { mergeUiMessageStream } from "./consume-ui-stream";
import {
  findSuccessfulDraftClauseLibraryWrite,
  isWriteToolName,
  normalizeProposalInput,
  type DraftClauseLibraryToolOutput,
  type ToolCallApprovalPayload,
} from "./types";

type Props = {
  className?: string;
  onClose?: () => void;
  selectedTemplateId: string;
  selectedTemplateName?: string;
  templateSections: DraftTemplateSectionOption[];
  clauses: DraftClauseAlternative[];
  onClauseWritten?: (clauseId: string) => void;
  onRefresh: (options?: { background?: boolean }) => Promise<void>;
  headerDragHandlers?: {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: (e: React.PointerEvent) => void;
    onPointerCancel: (e: React.PointerEvent) => void;
  };
};

const AUTO_CONTINUE_FOR_APPROVAL_PROMPT =
  "Invoke create-draft-clause-alternative (or update-draft-clause-alternative) now with the exact values from your last proposal so the UI can show approval.";

const EMPTY_PROMPTS = [
  "Add a balanced liability cap variant for the indemnification section",
  "List variants for the current template and suggest a new posture",
  "Update the language for an existing organization override",
] as const;

function messageTextContent(message: UIMessage): string {
  return message.parts
    .filter((part): part is Extract<UIMessage["parts"][number], { type: "text" }> => part.type === "text")
    .map((part) => part.text)
    .join("\n\n")
    .trim();
}

function uiMessageToChatMessage(
  message: UIMessage,
  options?: { hideProposalTable?: boolean }
): ChatMessage | null {
  if (isHiddenChatUiMessage(message)) return null;
  let text = messageTextContent(message);
  if (!text) return null;
  if (message.role === "assistant") {
    text = sanitizeAssistantChatDisplayText(text);
    text = assistantTextForChatDisplay(text, {
      hideProposalTable: options?.hideProposalTable,
    });
    if (!text) return null;
  }
  let createdAt: string | undefined;
  const raw = (message as UIMessage & { createdAt?: string | Date }).createdAt;
  if (typeof raw === "string") createdAt = raw;
  else if (raw instanceof Date) createdAt = raw.toISOString();
  return {
    id: message.id,
    role: message.role as "user" | "assistant",
    parts: [{ type: "text", text }],
    createdAt,
  };
}

function AssistantToolRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-row gap-2.5">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--avatar-assistant-border)] bg-[var(--avatar-assistant-bg)] dark:border-transparent dark:bg-gradient-to-br dark:from-blue-500 dark:to-purple-600">
        <Sparkles className="h-4 w-4 text-[var(--avatar-assistant-fg)] dark:text-white" />
      </div>
      <div className="min-w-0 max-w-[84%] flex-1">{children}</div>
    </div>
  );
}

export function DraftClauseLibraryAssistantPanel({
  className,
  onClose,
  selectedTemplateId,
  selectedTemplateName,
  templateSections,
  clauses,
  onClauseWritten,
  onRefresh,
  headerDragHandlers,
}: Props) {
  const { resolvedToolCallIds, markResolved, clearResolved } = useResolvedWriteApprovals();
  const [input, setInput] = useState("");
  const [resetConversationLoading, setResetConversationLoading] = useState(false);
  const [approvalByToolCallId, setApprovalByToolCallId] = useState<
    Record<string, ToolCallApprovalPayload>
  >({});
  const [actionLoading, setActionLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastSyncedWriteToolCallIdRef = useRef<string | null>(null);

  const sectionTitleByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const section of templateSections) {
      map.set(section.key, section.title);
    }
    return map;
  }, [templateSections]);

  const syncListAfterSuccessfulWrite = useCallback(
    async (messageList: UIMessage[]): Promise<boolean> => {
      const write = findSuccessfulDraftClauseLibraryWrite(messageList);
      if (!write) return false;
      if (lastSyncedWriteToolCallIdRef.current === write.toolCallId) return true;
      lastSyncedWriteToolCallIdRef.current = write.toolCallId;
      await onRefresh({ background: true });
      onClauseWritten?.(write.clauseId);
      return true;
    },
    [onClauseWritten, onRefresh]
  );

  const onWriteToolCallApprovalStreamPart = useMemo(
    () =>
      createWriteToolCallApprovalStreamHandler({
        isWriteToolName,
        normalizeProposalInput,
        setApprovalByToolCallId,
      }),
    [setApprovalByToolCallId]
  );

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: apiUrl("/api/draft-clause-library-chat"),
        credentials: "include",
        prepareSendMessagesRequest: async ({ body, headers, messages }) => {
          const token = await getJwtToken();
          const authHeaders: Record<string, string> = token
            ? { Authorization: `Bearer ${token}` }
            : {};
          return {
            body: {
              ...(body ?? {}),
              templateId: selectedTemplateId || undefined,
              messages: narrowUiMessagesForMastraThreadChat(messages),
            },
            headers: {
              ...(typeof headers === "object" && headers != null && !("append" in headers)
                ? (headers as Record<string, string>)
                : {}),
              ...authHeaders,
            },
          };
        },
      }),
    [selectedTemplateId]
  );

  const { messages, setMessages, sendMessage, status, error, clearError } = useChat({
    transport,
    onData: (dataPart) => {
      onWriteToolCallApprovalStreamPart(dataPart);
    },
    onFinish: ({ message, isError }) => {
      if (!isError) clearError();
      void syncListAfterSuccessfulWrite([...messages, message]);
    },
  });

  const appendUserChatMessage = useAppendUserChatMessage(messages, setMessages);

  const startNewConversation = useCallback(async () => {
    setResetConversationLoading(true);
    try {
      await resetWriteAssistantThread("/api/draft-clause-library-chat");
      setMessages([]);
      setApprovalByToolCallId({});
      clearResolved();
      lastSyncedWriteToolCallIdRef.current = null;
      clearError();
    } finally {
      setResetConversationLoading(false);
    }
  }, [clearError, clearResolved, setMessages]);

  useEffect(() => {
    void syncListAfterSuccessfulWrite(messages);
  }, [messages, syncListAfterSuccessfulWrite]);

  const activePending = useMemo(
    () =>
      findActiveWriteApproval({
        messages,
        approvalByToolCallId,
        isWriteToolName,
        normalizeProposalInput,
        resolvedToolCallIds,
      }),
    [messages, approvalByToolCallId, resolvedToolCallIds]
  );

  const { mergePendingApprovalsFromServer } = useMergePendingWriteApprovals({
    pendingUrl: apiUrl("/api/draft-clause-library-chat/pending"),
    status,
    messages,
    isWriteToolName,
    normalizeProposalInput,
    setApprovalByToolCallId,
    resolvedToolCallIds,
    pendingToolCallId: activePending?.approvalReady ? null : activePending?.toolCallId,
    approvalReady: activePending?.approvalReady,
  });

  const enrichClauseProposal = useCallback(
    (proposal: Record<string, unknown>) => ({
      ...proposal,
      template_id:
        (typeof proposal.template_id === "string" && proposal.template_id.trim()) ||
        selectedTemplateId ||
        "",
    }),
    [selectedTemplateId]
  );

  const { editablePlan, mergePlanDraftForApprove } = useWriteApprovalPlanDraftSync({
    kind: "draft-clause-library",
    activePending: activePending
      ? {
          toolCallId: activePending.toolCallId,
          approvalReady: activePending.approvalReady,
          proposal: activePending.proposal,
        }
      : null,
    isWriteToolName,
    setMessages,
    setApprovalByToolCallId,
    enrichProposal: enrichClauseProposal,
  });

  useAutoContinueTextOnlyWriteProposal({
    status,
    messages,
    actionLoading,
    activePending,
    continuePrompt: AUTO_CONTINUE_FOR_APPROVAL_PROMPT,
    messageTextContent,
    isWriteToolName,
    resolvedToolCallIds,
    sendMessage,
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, status, activePending?.toolCallId]);

  const clearApproval = useCallback(
    (toolCallId: string) => {
      markResolved(toolCallId);
      setApprovalByToolCallId((prev) => {
        const copy = { ...prev };
        delete copy[toolCallId];
        return copy;
      });
    },
    [markResolved]
  );

  const { open: planOpen, setOpen: setPlanOpen } = useAssistantPlan();

  const postApprovalAction = useCallback(
    async (
      path: "approve" | "decline",
      body: Record<string, unknown>,
      messagesForRequest?: UIMessage[],
      options?: { declineReason?: string }
    ) => {
      const requestMessages = dedupeUiMessagesForApiRequest(messagesForRequest ?? messages);
      const pendingSnapshot = findActiveWriteApproval({
        messages: requestMessages,
        approvalByToolCallId,
        isWriteToolName,
        normalizeProposalInput,
        resolvedToolCallIds,
      });
      setActionLoading(true);
      try {
        const token = await getJwtToken();
        const response = await fetch(apiUrl(`/api/draft-clause-library-chat/${path}`), {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ ...body, messages: requestMessages, templateId: selectedTemplateId }),
        });
        const settledToolCallId = typeof body.toolCallId === "string" ? body.toolCallId : "";
        let nextMessages = requestMessages;

        const parsed = await parseWriteAssistantApproveResponse({
          response,
          path,
          requestMessages,
          settledToolCallId,
          directWrite: {
            entityKey: "draftClauseAlternative",
            isWriteToolName,
            buildOutput: (entity) => ({
              success: true,
              draftClauseAlternative: {
                id: String(entity.id ?? ""),
                section_key: String(entity.section_key ?? ""),
                clause_category:
                  typeof entity.clause_category === "string" ? entity.clause_category : null,
                language: typeof entity.language === "string" ? entity.language : undefined,
              },
            }),
          },
        });

        if (parsed.handledAsJson) {
          nextMessages = parsed.nextMessages;
          if (nextMessages !== requestMessages) {
            setMessages(nextMessages);
          }
        } else {
          nextMessages = await mergeUiMessageStream(
            response,
            requestMessages,
            setMessages,
            { onStreamPart: onWriteToolCallApprovalStreamPart }
          );
          const declineReason =
            options?.declineReason ??
            (typeof body.reason === "string" ? body.reason : undefined);
          nextMessages = finalizeWriteApprovalStreamMessages({
            messages: nextMessages,
            toolCallId: settledToolCallId,
            isWriteToolName,
            path,
            declineReason: path === "decline" ? declineReason : undefined,
          });
          setMessages(nextMessages);
          await mergePendingApprovalsFromServer();
        }

        clearError();
        if (path === "approve") {
          if (settledToolCallId && parsed.nextMessages !== requestMessages) {
            clearApproval(settledToolCallId);
          }
          const synced = await syncListAfterSuccessfulWrite(nextMessages);
          if (!synced) {
            await onRefresh({ background: true });
          }
          const write = findSuccessfulDraftClauseLibraryWrite(nextMessages);
          const toolCallId = typeof body.toolCallId === "string" ? body.toolCallId : "";
          const pendingSection =
            typeof pendingSnapshot?.proposal.section_key === "string"
              ? pendingSnapshot.proposal.section_key
              : "";
          const displayName =
            write?.label ??
            (pendingSection ||
              (toolCallId ? approvalByToolCallId[toolCallId]?.args?.section_key : "") ||
              "Clause variant");
          applyWriteApprovalSuccessMessageIfNeeded({
            kind: "draft-clause-library",
            mode: pendingSnapshot?.mode ?? "create",
            messages: nextMessages,
            setMessages,
            displayName,
          });
        }
      } catch (err: unknown) {
        const raw = err instanceof Error ? err.message : String(err);
        throw new Error(mapAssistantChatUserError(raw));
      } finally {
        setActionLoading(false);
      }
    },
    [
      messages,
      setMessages,
      syncListAfterSuccessfulWrite,
      approvalByToolCallId,
      resolvedToolCallIds,
      onRefresh,
      clearError,
      selectedTemplateId,
      mergePendingApprovalsFromServer,
      onWriteToolCallApprovalStreamPart,
      clearApproval,
    ]
  );

  const isStreaming = status === "streaming" || status === "submitted";
  const busy = isStreaming || actionLoading;

  const onViewPlan = useCallback(() => setPlanOpen(true), [setPlanOpen]);

  const { pendingDockActions, revisePendingProposal } = useAssistantWriteApprovalHandlers({
    activePending: activePending
      ? {
          toolCallId: activePending.toolCallId,
          runId: activePending.runId,
          mode: activePending.mode,
          approvalReady: activePending.approvalReady,
        }
      : null,
    busy,
    appendUserChatMessage,
    postApprovalAction,
    clearApproval,
    onViewPlan,
  });

  const handlePlanCreate = useCallback(() => {
    if (!activePending?.approvalReady) return;
    const merged = mergePlanDraftForApprove();
    if (!planDraftReadyForDirectWrite("draft-clause-library", editablePlan?.draft ?? {})) return;
    const userText = approvalChoiceUserText({
      choice: "create",
      mode: activePending.mode === "update" ? "update" : "create",
    });
    const withUser = appendUserChatMessage(userText, WRITE_APPROVAL_CHOICE_MESSAGE_METADATA);
    void postApprovalAction(
      "approve",
      {
        runId: activePending.runId,
        toolCallId: activePending.toolCallId,
        proposalOverride: merged,
      },
      withUser
    );
  }, [
    activePending,
    mergePlanDraftForApprove,
    editablePlan?.draft,
    appendUserChatMessage,
    postApprovalAction,
  ]);

  useSyncAssistantPlan({
    kind: "draft-clause-library",
    activePending: activePending
      ? {
          mode: activePending.mode,
          proposal: activePending.proposal,
          approvalReady: activePending.approvalReady,
        }
      : null,
    disabled: busy || !activePending?.approvalReady,
    busy,
    onCreate: handlePlanCreate,
    editablePlan,
  });

  async function submitMessage() {
    const text = input.trim();
    if (!text || !selectedTemplateId) return;
    setInput("");
    await submitAssistantChatMessage({
      text,
      status,
      actionLoading,
      activePending,
      appendUserChatMessage,
      revisePendingProposal,
      sendMessage,
    });
  }

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    await submitMessage();
  }

  function renderToolPart(part: UIMessage["parts"][number]) {
    if (!isToolUIPart(part)) return null;
    const toolName = getToolName(part);
    if (!isWriteToolName(toolName)) return null;

    const mode = toolName.toLowerCase().includes("update") ? "update" : "create";
    const inputData = normalizeProposalInput(part.input);
    const approval = approvalByToolCallId[part.toolCallId];
    const existing =
      inputData.id != null
        ? clauses.find((c) => c.id === inputData.id) ?? null
        : clauses.find(
            (c) =>
              c.section_key === (inputData.section_key ?? "") &&
              (c.clause_category ?? null) === (inputData.clause_category ?? null) &&
              (c.posture ?? null) === (inputData.posture ?? null)
          ) ?? null;

    const sectionKey =
      inputData.section_key ?? approval?.args.section_key ?? existing?.section_key ?? "";
    const sectionTitle = sectionTitleByKey.get(sectionKey);

    if (
      part.state === "input-available" ||
      part.state === "approval-requested" ||
      (part.state === "input-streaming" && part.input)
    ) {
      if (resolvedToolCallIds.has(part.toolCallId)) {
        return null;
      }
      if (isActivePendingWriteToolPart(part, activePending)) {
        return null;
      }
      if (!approval?.runId) {
        return (
          <p className="text-xs text-[#576274] dark:text-gray-400">
            Waiting for approval context…
          </p>
        );
      }
      return (
        <DraftClauseLibraryProposalCard
          mode={mode}
          proposal={approval.args.id ? approval.args : inputData}
          existing={mode === "update" ? existing : null}
          sectionTitle={sectionTitle}
          disabled={actionLoading || status === "streaming"}
          showActions={false}
        />
      );
    }

    if (part.state === "output-available") {
      const output = (part.output ?? {}) as DraftClauseLibraryToolOutput;
      if (isUserRejectedWriteToolOutput(output)) return null;
      return <DraftClauseLibraryResultCard mode={mode} output={output} />;
    }

    if (part.state === "output-error") {
      return <DraftClauseLibraryResultCard mode={mode} output={{}} errorText={part.errorText} />;
    }

    return null;
  }

  const canSubmit = Boolean(input.trim()) && !busy && Boolean(selectedTemplateId);

  const streamingAssistantText = useMemo(() => {
    if (!isStreaming) return "";
    const last = messages.length > 0 ? messages[messages.length - 1] : undefined;
    if (last?.role !== "assistant") return "";
    return messageTextContent(last);
  }, [isStreaming, messages]);

  const showThinking = isStreaming && !streamingAssistantText && !actionLoading;

  const headerSubtitle = headerDragHandlers
    ? "Drag header to move panel"
    : planOpen
      ? "Reviewing proposal"
      : selectedTemplateName
        ? `Working on: ${selectedTemplateName}`
        : "Create or update clause variants with approval";

  return (
    <AssistantPanelPlanSplit
      className={cn("dark:bg-gradient-to-b dark:from-slate-800 dark:to-slate-900", className)}
      header={
        <div
          className={cn(
            "flex shrink-0 items-center justify-between border-b border-[rgba(15,23,42,0.09)] bg-gradient-to-r from-blue-600/[0.08] to-purple-600/[0.08] px-4 py-3 dark:border-white/10 dark:from-blue-600/20 dark:to-purple-600/20",
            headerDragHandlers && "cursor-grab touch-none active:cursor-grabbing"
          )}
          {...(headerDragHandlers ?? {})}
        >
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg shadow-blue-500/20">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="truncate text-sm font-semibold text-[#101828] dark:text-white">
                Clause Library Assistant
              </h3>
              <p className="truncate text-xs text-[#576274] dark:text-gray-400">{headerSubtitle}</p>
            </div>
          </div>
          {onClose ? (
            <IconButton
              type="button"
              size="sm"
              onClick={onClose}
              title="Close chat"
              aria-label="Close chat"
            >
              <X className="h-4 w-4" />
            </IconButton>
          ) : null}
        </div>
      }
      messageArea={
        <div
          ref={scrollRef}
          className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-none"
        >
        <div
          className={cn(
            "p-4",
            messages.length === 0 ? "flex min-h-full flex-col" : "space-y-4"
          )}
        >
          {messages.length === 0 ? (
            <AssistantChatEmptyState
              intro="Describe a clause variant to create or update for the selected template. I will show a review proposal—use View Plan to inspect details, request changes in chat, or Create when ready."
              examples={EMPTY_PROMPTS}
              onSelectExample={setInput}
              disabled={busy || !selectedTemplateId}
            />
          ) : null}
          {messages.map((message) => {
            const chatMessage = uiMessageToChatMessage(message, {
              hideProposalTable: Boolean(activePending),
            });
            const toolRows =
              message.role === "assistant"
                ? message.parts.flatMap((part, index) => {
                    const toolUi = renderToolPart(part);
                    if (!toolUi) return [];
                    return [
                      <AssistantToolRow key={`${message.id}-tool-${index}`}>
                        {toolUi}
                      </AssistantToolRow>,
                    ];
                  })
                : [];
            if (!chatMessage && toolRows.length === 0) return null;
            return (
              <div key={message.id} className="space-y-3">
                {chatMessage ? <ChatMessageBubble message={chatMessage} /> : null}
                {toolRows}
              </div>
            );
          })}

          {activePending && pendingDockActions ? (
            <AssistantWriteApprovalThreadMessage
              kind="draft-clause-library"
              mode={activePending.mode}
              approvalReady={activePending.approvalReady}
              proposal={activePending.proposal}
              disabled={pendingDockActions?.disabled}
              onViewPlan={pendingDockActions?.onViewPlan ?? (() => {})}
              onCreate={handlePlanCreate}
            />
          ) : null}

          {showThinking ? (
            <div className="group flex flex-row gap-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--avatar-assistant-border)] bg-[var(--avatar-assistant-bg)] dark:border-transparent dark:bg-gradient-to-br dark:from-blue-500 dark:to-purple-600">
                <Sparkles className="h-4 w-4 text-[var(--avatar-assistant-fg)] dark:text-white" />
              </div>
              <div className="relative max-w-[84%] rounded-2xl rounded-tl-sm border border-[rgba(15,23,42,0.09)] bg-white px-4 py-3 text-sm text-[#101828] shadow-[var(--shadow-card)] dark:border-white/10 dark:bg-white/10 dark:text-white dark:shadow-none">
                <div className="flex items-center gap-2 text-[#576274] dark:text-gray-300">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Thinking...</span>
                </div>
              </div>
            </div>
          ) : null}

          {error ? (
            <AssistantChatErrorNotice
              error={error}
              onStartNewConversation={startNewConversation}
              startNewConversationLoading={resetConversationLoading}
            />
          ) : null}
        </div>
        </div>
      }
      composer={
        <MessageComposer
          compact
          value={input}
          onChange={setInput}
          onSubmit={handleSend}
          disabled={busy || !selectedTemplateId}
          canSubmit={canSubmit}
          placeholder={
            !selectedTemplateId
              ? "Select a draft template first…"
              : activePending
                ? "Request a change to the proposal…"
                : "Type your message..."
          }
        >
          {activePending ? (
            <AssistantPendingProposalComposerExtras
              kind="draft-clause-library"
              proposal={activePending.proposal}
              disabled={busy || !selectedTemplateId}
              onSelectSuggestion={setInput}
            />
          ) : null}
        </MessageComposer>
      }
    />
  );
}
